import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { apiClient, API_PATHS } from '@/services/api';
import { ESettingsEnumType } from '@/constants/settings';

type CountryEnumItem = string | { value: string };

// Country enums are static; cache the in-flight request so all callers share one call.
let countryEnumsPromise: Promise<CountryEnumItem[]> | null = null;

function loadCountryEnums(): Promise<CountryEnumItem[]> {
  if (!countryEnumsPromise) {
    countryEnumsPromise = apiClient.rfp
      .get(API_PATHS.rfp.settings.enums, { params: { type: ESettingsEnumType.COUNTRIES } })
      .then((res) => res.data.data ?? [])
      .catch((err) => {
        countryEnumsPromise = null;
        throw err;
      });
  }
  return countryEnumsPromise;
}

/**
 * Country codes from the settings enums endpoint, mapped to localized
 * `{ value, label }` options (value = ISO code, label = translated name from
 * `global.countries.*`). The request is cached across all callers.
 */
export function useCountryOptions() {
  const { t } = useI18n();
  const countries = ref<CountryEnumItem[]>([]);

  const countryOptions = computed(() =>
    countries.value.map((c) => {
      const code = typeof c === 'string' ? c : c.value;
      return { value: code, label: t(`global.countries.${code}`, code) };
    }),
  );

  onMounted(async () => {
    try {
      countries.value = await loadCountryEnums();
    } catch {
      /* countries list is best-effort */
    }
  });

  return { countries, countryOptions };
}
